import Emitter from '../../core/Emitter'
import EventBus from '../../core/EventBus'
import {
    getScrollTop as getScrollTopNative,
    getScroll as getScrollNative,
    enableScrolling as enableScrollingNative,
    disableScrolling as disableScrollingNative,
} from '../Viewport/scroll'
import scrollToNative from '../Viewport/scrollTo'
import throttle from 'lodash/throttle'
import Scrollbar from 'smooth-scrollbar'
import OverscrollPlugin from 'smooth-scrollbar/plugins/overscroll'
import TouchMouseInputResolver from '../../meta/TouchMouseInputResolver'

// Scrollbar.use(OverscrollPlugin)

import { default as scrollToElementNative } from '../Viewport/scrollToElement'

class Scroll extends Emitter {
    constructor(context = window) {
        super()

        this.context = context
        this.current = {
            x: 0,
            y: 0,
        }
        this.raf = null
        this.container = document.querySelector('.scroll-Container')

        this.handleResize = throttle(this.handleResize, 50)

        window.addEventListener('resize', this.handleResize)
        window.addEventListener('scroll', this.handleNativeScroll)
        EventBus.on('contentchange', this.handleResize)

        this.scrollbar = null
        this.isVirtual = false
        this.lastAccessibilityScrollTarget = null

        this.staticLimit = {
            x: this.container.offsetWidth - window.innerWidth,
            y: this.container.offsetHeight - window.innerHeight,
        }
    }

    getScroll() {
        if (this.isVirtual) {
            return {
                x: this.scrollbar.scrollLeft,
                y: this.scrollbar.scrollTop,
            }
        } else {
            return getScrollNative()
        }
    }

    handleNativeScroll = () => {
        if (this.isVirtual) {
            return
        }

        this.current = getScrollNative()

        this.render()
    }

    handleResize = () => {
        this.resize()
    }

    handleVirtualScroll = (status) => {
        this.current = {
            x: status.offset.x,
            y: status.offset.y,
        }

        this.render()
    }

    // Přemostění pro fokus: fokus dovnitř obsahu posune virtuální scroll tak, aby byl
    // fokusovaný prvek vidět – ve virtuálních souřadnicích, takže funguje OBĚMA směry
    // (na rozdíl od nativního můstku, který zvládá jen odhalování obsahu pod scrollportem).
    // Pokrývá klávesovou navigaci, programový fokus i focus-mode čteček; onlyScrollIfNeeded
    // brání zbytečnému poskakování u prvků, které už vidět jsou.
    handleAccessibilityFocus = (event) => {
        if (!this.isVirtual || !this.scrollbar) {
            return
        }

        const target = event.target

        // scrollujeme jen prvky uvnitř scrollovaného kontejneru – ne fixed overlaye a modály
        if (!target || !this.container.contains(target)) {
            return
        }

        this.scrollbar.scrollIntoView(target, {
            offsetTop: 80,
            onlyScrollIfNeeded: true,
        })
    }

    // Hlavní přemostění pro čtení čtečkou v prohlížecím režimu (nadpisy, odstavce, seznamy –
    // tedy NEfokusovatelný text). Čtečka při pohybu virtuálního kurzoru posouvá DOM selekci;
    // podle konce selekce posuneme virtuální scroll na daný prvek. Používá virtuální
    // souřadnice smooth-scrollbaru (scrollIntoView), takže funguje spolehlivě OBĚMA směry –
    // na rozdíl od nativního scrollu, který smooth-scrollbar drží na 0 a počítá nekonzistentně.
    // lastAccessibilityScrollTarget brání opakovanému scrollování stejného prvku (např. při
    // tažení výběru myší), onlyScrollIfNeeded pak zbytečnému poskakování u viditelných prvků.
    handleAccessibilitySelection = () => {
        if (!this.isVirtual || !this.scrollbar) {
            return
        }

        const selection = document.getSelection()

        if (!selection || selection.rangeCount === 0) {
            return
        }

        const focusNode = selection.focusNode

        if (!focusNode) {
            return
        }

        const element = focusNode.nodeType === Node.TEXT_NODE ? focusNode.parentElement : focusNode

        if (!element || !this.container.contains(element) || element === this.lastAccessibilityScrollTarget) {
            return
        }

        this.lastAccessibilityScrollTarget = element

        this.scrollbar.scrollIntoView(element, {
            offsetTop: 80,
            onlyScrollIfNeeded: true,
        })
    }

    render = () => {
        this.emit('scroll', {
            offset: { ...this.current },
        })
    }

    resize = () => {
        if (!this.isVirtual) {
            this.staticLimit = {
                x: this.container.offsetWidth - window.innerWidth,
                y: this.container.offsetHeight - window.innerHeight,
            }
        }
        this.emit('resize')
    }

    setPosition(x, y) {
        y = y === 0 ? -80 : y //ios fix, overscroll nulovou pozici kvuli liste
        if (this.isVirtual) {
            this.scrollbar.setPosition(x, y)
        } else {
            window.scrollTo(x, y)
        }
    }

    useNative(hash = null) {
        this.isVirtual = false
        this.container.classList.remove('is-virtual')
        document.removeEventListener('focusin', this.handleAccessibilityFocus)
        document.removeEventListener('selectionchange', this.handleAccessibilitySelection)

        if (hash) {
            this.scrollToAnchor(hash)
        }
    }

    useVirtual(hash = null) {
        this.isVirtual = true
        this.container.classList.add('is-virtual')

        if (!this.scrollbar) {
            // Přemostění virtuálního scrollu pro čtečky – řídí smooth-scrollbar přes jeho
            // vlastní scrollIntoView (virtuální souřadnice, funguje oběma směry).
            document.addEventListener('focusin', this.handleAccessibilityFocus)
            document.addEventListener('selectionchange', this.handleAccessibilitySelection)

            this.scrollbar = Scrollbar.init(this.container, {
                renderByPixels: false,
            })
            this.scrollbar.addListener(this.handleVirtualScroll)
        }

        if (hash) {
            this.scrollToAnchor(hash)
        }
    }

    getLimit() {
        if (this.isVirtual && this.scrollbar) {
            return this.scrollbar.limit
        } else {
            return this.staticLimit
        }
    }

    scrollToAnchor(hash) {
        const target = document.getElementById(hash.replace('#', ''))

        if (target) {
            setTimeout(() => {
                scrollToElement(target, {
                    animate: false,
                })
            }, 1)
        }
    }
}

const scrollInstance = new Scroll()

const _defaults = {
    offset: 80,
    animate: true,
}

export function scrollToElement(element, options = {}) {
    options = {
        ..._defaults,
        ...options,
    }

    if (scrollInstance.isVirtual) {
        if (options.animate) {
            scrollInstance.scrollbar.scrollIntoView(element, {
                offsetTop: options.offset ? options.offset : 0,
                onlyScrollIfNeeded: options.loose,
                alignToTop: true,
            })
        } else {
            const box = element.getBoundingClientRect()
            scrollInstance.scrollbar.setPosition(0, box.top - options.offset)
        }
    } else {
        scrollToElementNative(element, options)
    }
}

export function scrollTo(y) {
    if (scrollInstance.isVirtual) {
        scrollInstance.scrollbar.scrollTo(0, y, 600)
    } else {
        scrollToNative(y)
    }
}

export function getScroll() {
    if (scrollInstance.isVirtual) {
        return scrollInstance.scrollbar.offset
    } else {
        return getScrollNative()
    }
}

export function getScrollTop() {
    if (scrollInstance.isVirtual) {
        return scrollInstance.scrollbar.scrollTop
    } else {
        return getScrollTopNative()
    }
}

export function disableScrolling() {
    disableScrollingNative()
}

export function enableScrolling() {
    enableScrollingNative()
}

window.scrollInstance = scrollInstance

export default scrollInstance
